import { createSlice, type PayloadAction } from '@reduxjs/toolkit';
import type { SurveyLanguage } from '@shared/types/surveyLanguage';
import { isSurveyLanguage, SURVEY_LANGUAGE_STORAGE_KEY } from '@shared/constants/surveyLanguage';
import { STAFF_REF_STORAGE_KEY } from '@shared/constants/staffRef';
import { clearOtherPrevBrandAnswerAnswers } from '@/lib/otherPrevBrandAnswerStorage';
import { clearOtherPrevBrandAnswers } from '@/lib/otherPrevBrandStorage';
import { clearWhyNotLdAnswers } from '@/lib/whyNotLdStorage';
import { clearPrevBrandAnswerAnswers } from '@/lib/prevBrandAnswerStorage';
import { clearPrevBrandAnswers } from '@/lib/prevBrandStorage';
import { clearSetAProfileAnswers } from '@/lib/setAProfileStorage';

function readStoredSurveyLanguage(): SurveyLanguage | null {
  try {
    const raw = sessionStorage.getItem(SURVEY_LANGUAGE_STORAGE_KEY);
    return isSurveyLanguage(raw) ? raw : null;
  } catch {
    return null;
  }
}

function persistSurveyLanguage(lang: SurveyLanguage | null) {
  try {
    if (lang === null) sessionStorage.removeItem(SURVEY_LANGUAGE_STORAGE_KEY);
    else sessionStorage.setItem(SURVEY_LANGUAGE_STORAGE_KEY, lang);
  } catch {
    /* private mode / quota */
  }
}

export const SURVEY_SCREENING_STORAGE_KEY = 'ldsurvey:surveyScreening';

export type SurveySmokerAnswer = 'yes' | 'no';

export type SurveyScreeningAnswers = {
  ageBand: string;
  smoker: SurveySmokerAnswer;
};

function isSurveySmokerAnswer(v: unknown): v is SurveySmokerAnswer {
  return v === 'yes' || v === 'no';
}

function readStoredSurveyScreening(): SurveyScreeningAnswers | null {
  try {
    const raw = sessionStorage.getItem(SURVEY_SCREENING_STORAGE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as unknown;
    if (
      typeof parsed !== 'object' ||
      parsed === null ||
      typeof (parsed as { ageBand?: unknown }).ageBand !== 'string' ||
      !isSurveySmokerAnswer((parsed as { smoker?: unknown }).smoker)
    ) {
      return null;
    }
    return {
      ageBand: (parsed as SurveyScreeningAnswers).ageBand,
      smoker: (parsed as SurveyScreeningAnswers).smoker,
    };
  } catch {
    return null;
  }
}

function persistSurveyScreening(data: SurveyScreeningAnswers | null) {
  try {
    if (data === null) sessionStorage.removeItem(SURVEY_SCREENING_STORAGE_KEY);
    else sessionStorage.setItem(SURVEY_SCREENING_STORAGE_KEY, JSON.stringify(data));
  } catch {
    /* ignore */
  }
}

export const PDPA_CONSENT_STORAGE_KEY = 'ldsurvey:pdpaConsent';

function readStoredPdpaConsent(): boolean {
  try {
    return sessionStorage.getItem(PDPA_CONSENT_STORAGE_KEY) === 'true';
  } catch {
    return false;
  }
}

function persistPdpaConsent(value: boolean) {
  try {
    sessionStorage.setItem(PDPA_CONSENT_STORAGE_KEY, value ? 'true' : 'false');
  } catch {
    /* ignore */
  }
}

export const OTP_DRAFT_STORAGE_KEY = 'ldsurvey:otpDraft';

export type OtpDraft = {
  mobileLocalDigits: string;
  customerUuid?: string;
  otpSentAtMs?: number;
  otpVerified?: boolean;
  submissionIndex?: number;
  questionnaireSet?: 'A' | 'B';
};

function readStoredOtpDraft(): OtpDraft {
  try {
    const raw = sessionStorage.getItem(OTP_DRAFT_STORAGE_KEY);
    if (!raw) return { mobileLocalDigits: '' };
    const parsed = JSON.parse(raw) as unknown;
    if (typeof parsed !== 'object' || parsed === null) {
      return { mobileLocalDigits: '' };
    }
    const o = parsed as {
      mobileLocalDigits?: unknown;
      customerUuid?: unknown;
      otpSentAtMs?: unknown;
      otpVerified?: unknown;
      submissionIndex?: unknown;
      questionnaireSet?: unknown;
    };
    const otpSentAtMs =
      typeof o.otpSentAtMs === 'number' && Number.isFinite(o.otpSentAtMs) ? o.otpSentAtMs : undefined;
    const submissionIndex =
      typeof o.submissionIndex === 'number' && Number.isFinite(o.submissionIndex)
        ? o.submissionIndex
        : undefined;
    const questionnaireSet =
      o.questionnaireSet === 'A' || o.questionnaireSet === 'B' ? o.questionnaireSet : undefined;
    return {
      mobileLocalDigits: typeof o.mobileLocalDigits === 'string' ? o.mobileLocalDigits : '',
      customerUuid: typeof o.customerUuid === 'string' ? o.customerUuid : undefined,
      otpVerified: o.otpVerified === true ? true : undefined,
      ...(otpSentAtMs !== undefined ? { otpSentAtMs } : {}),
      ...(submissionIndex !== undefined ? { submissionIndex } : {}),
      ...(questionnaireSet !== undefined ? { questionnaireSet } : {}),
    };
  } catch {
    return { mobileLocalDigits: '' };
  }
}

function persistOtpDraft(data: OtpDraft) {
  try {
    sessionStorage.setItem(OTP_DRAFT_STORAGE_KEY, JSON.stringify(data));
  } catch {
    /* ignore */
  }
}

export type StaffRef = {
  staffCode: string;
  staffName: string;
};

function readStoredStaffRef(): StaffRef | null {
  try {
    const raw = sessionStorage.getItem(STAFF_REF_STORAGE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as unknown;
    if (
      typeof parsed !== 'object' ||
      parsed === null ||
      typeof (parsed as { staffCode?: unknown }).staffCode !== 'string' ||
      typeof (parsed as { staffName?: unknown }).staffName !== 'string'
    ) {
      return null;
    }
    const staffCode = (parsed as StaffRef).staffCode.trim();
    if (!staffCode) return null;
    return {
      staffCode,
      staffName: (parsed as StaffRef).staffName,
    };
  } catch {
    return null;
  }
}

function persistStaffRef(data: StaffRef | null) {
  try {
    if (data === null) sessionStorage.removeItem(STAFF_REF_STORAGE_KEY);
    else sessionStorage.setItem(STAFF_REF_STORAGE_KEY, JSON.stringify(data));
  } catch {
    /* ignore */
  }
}

const appSlice = createSlice({
  name: 'app',
  initialState: {
    surveyLanguage: readStoredSurveyLanguage(),
    surveyScreening: readStoredSurveyScreening() as SurveyScreeningAnswers | null,
    pdpaConsent: readStoredPdpaConsent(),
    otpDraft: readStoredOtpDraft(),
    staffRef: readStoredStaffRef() as StaffRef | null,
  },
  reducers: {
    setSurveyLanguage(state, action: PayloadAction<SurveyLanguage>) {
      state.surveyLanguage = action.payload;
      persistSurveyLanguage(action.payload);
    },
    clearSurveyLanguage(state) {
      state.surveyLanguage = null;
      persistSurveyLanguage(null);
    },
    setSurveyScreeningAnswers(state, action: PayloadAction<SurveyScreeningAnswers>) {
      state.surveyScreening = action.payload;
      persistSurveyScreening(action.payload);
    },
    clearSurveyScreeningAnswers(state) {
      state.surveyScreening = null;
      persistSurveyScreening(null);
      state.pdpaConsent = false;
      persistPdpaConsent(false);
      state.otpDraft = { mobileLocalDigits: '' };
      persistOtpDraft({ mobileLocalDigits: '' });
      clearSetAProfileAnswers();
      clearPrevBrandAnswers();
      clearPrevBrandAnswerAnswers();
      clearWhyNotLdAnswers();
      clearOtherPrevBrandAnswers();
      clearOtherPrevBrandAnswerAnswers();
    },
    setPdpaConsent(state, action: PayloadAction<boolean>) {
      state.pdpaConsent = action.payload;
      persistPdpaConsent(action.payload);
    },
    setOtpDraft(state, action: PayloadAction<OtpDraft>) {
      state.otpDraft = action.payload;
      persistOtpDraft(action.payload);
    },
    setStaffRef(state, action: PayloadAction<StaffRef>) {
      state.staffRef = action.payload;
      persistStaffRef(action.payload);
    },
    clearStaffRef(state) {
      state.staffRef = null;
      persistStaffRef(null);
    },
    resetAppForNewCustomer(state) {
      state.surveyLanguage = null;
      state.surveyScreening = null;
      state.pdpaConsent = false;
      state.otpDraft = { mobileLocalDigits: '' };
      state.staffRef = null;
      persistSurveyLanguage(null);
      persistSurveyScreening(null);
      persistPdpaConsent(false);
      persistOtpDraft({ mobileLocalDigits: '' });
      persistStaffRef(null);
      clearSetAProfileAnswers();
      clearPrevBrandAnswers();
      clearPrevBrandAnswerAnswers();
      clearWhyNotLdAnswers();
      clearOtherPrevBrandAnswers();
      clearOtherPrevBrandAnswerAnswers();
    },
  },
});

export const {
  setSurveyLanguage,
  clearSurveyLanguage,
  setSurveyScreeningAnswers,
  clearSurveyScreeningAnswers,
  setPdpaConsent,
  setOtpDraft,
  setStaffRef,
  clearStaffRef,
  resetAppForNewCustomer,
} = appSlice.actions;

export default appSlice.reducer;
